import { motion, useScroll, useTransform } from "framer-motion";
import { LOGO_URL, HERO_BG } from "@/lib/content";
import Hero3D from "@/components/site/Hero3D";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay },
});

export default function Hero() {
  const { scrollY } = useScroll();
  // Subtle parallax on the crest as the hero scrolls out
  const crestY = useTransform(scrollY, [0, 600], [0, -60]);
  const crestRot = useTransform(scrollY, [0, 600], [0, -4]);
  const crestScale = useTransform(scrollY, [0, 600], [1, 0.94]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-ink/85" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/70 to-ink" aria-hidden />

      {/* 3D wireframe candlestick field */}
      <Hero3D />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-40 lg:pb-48">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div {...fade(0)} className="lg:col-span-3 flex lg:justify-start">
            <motion.img
              src={LOGO_URL}
              alt="KP Capital — Private Trading Society"
              data-testid="hero-crest"
              className="w-48 md:w-60 lg:w-full max-w-[260px] object-contain select-none drop-shadow-[0_0_40px_rgba(229,228,226,0.08)]"
              draggable={false}
              style={{ y: crestY, rotate: crestRot, scale: crestScale }}
            />
          </motion.div>

          <div className="lg:col-span-9">
            <motion.span {...fade(0.1)} className="overline" data-testid="hero-overline">
              KP Capital — Private Trading Society
            </motion.span>

            <motion.h1
              {...fade(0.2)}
              data-testid="hero-headline"
              className="font-serif font-medium text-[2.75rem] sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight mt-6 text-bone"
            >
              The trade you keep losing
              <br />
              is not a trade.
              <br />
              <span className="text-platinum italic font-light">It is you.</span>
            </motion.h1>

            <motion.p
              {...fade(0.35)}
              data-testid="hero-subtext"
              className="font-sans text-sand text-base md:text-xl leading-relaxed mt-10 max-w-2xl"
            >
              Private 1-on-1 mentorship in trading psychology for serious operators.
              No signals. No groups. No theatre. Just the deep, uncomfortable, decisive
              work of becoming the trader your strategy already deserves.
            </motion.p>

            <motion.div {...fade(0.5)} className="mt-14 flex flex-col sm:flex-row gap-4 sm:gap-6">
              <a
                href="#apply"
                data-testid="hero-apply-btn"
                className="group inline-flex items-center justify-center gap-4 bg-platinum text-ink px-8 py-4 text-xs tracking-[0.3em] uppercase font-medium hover:bg-bone transition-colors"
              >
                Apply for Consultation
                <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#approach"
                data-testid="hero-approach-btn"
                className="inline-flex items-center justify-center gap-4 border border-hairline text-bone px-8 py-4 text-xs tracking-[0.3em] uppercase hover:border-platinum hover:bg-elevated transition-colors"
              >
                The Approach
              </a>
            </motion.div>
          </div>
        </div>

        <motion.div
          {...fade(0.7)}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase text-smoke flex flex-col items-center gap-3"
          aria-hidden
        >
          <span>Scroll</span>
          <span className="h-10 w-px bg-gradient-to-b from-smoke to-transparent" />
        </motion.div>
      </div>
    </section>
  );
}
